/**
 * VoiceController Component
 * Main controller for voice interaction with state management
 */

import React, { useCallback } from 'react';
import type { VoiceControllerProps } from '../../types';
import { useVoice } from '../../hooks/useVoice';
import { VoiceBubble } from './VoiceBubble';
import { VoiceBar } from './VoiceBar';
import { VoicePanel } from './VoicePanel';

export const VoiceController: React.FC<VoiceControllerProps> = ({
  layout,
  position = 'bottom-right',
  onTranscript,
  onStateChange,
  silenceThreshold = 2000,
  language = 'es-ES',
}) => {
  const {
    voiceState,
    transcript,
    startRecording,
    stopRecording,
  } = useVoice({
    silenceThreshold,
    language,
    onTranscript,
  });

  const [isOpen, setIsOpen] = React.useState(false);

  // Notify parent of state changes
  React.useEffect(() => {
    if (onStateChange) {
      onStateChange(voiceState);
    }
  }, [voiceState, onStateChange]);

  const handleStart = useCallback(() => {
    startRecording();
  }, [startRecording]);

  const handleStop = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const handleToggle = useCallback(() => {
    if (layout === 'bubble') {
      if (voiceState === 'listening') {
        stopRecording();
      } else if (voiceState === 'idle') {
        startRecording();
      }
    } else {
      setIsOpen(!isOpen);
    }
  }, [layout, voiceState, isOpen, startRecording, stopRecording]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (voiceState === 'listening') {
      stopRecording();
    }
  }, [voiceState, stopRecording]);

  // Render based on layout
  switch (layout) {
    case 'bubble':
      return (
        <VoiceBubble
          voiceState={voiceState}
          position={position}
          onClick={handleToggle}
        />
      );

    case 'bar':
      return (
        <>
          <VoiceBubble
            voiceState={voiceState}
            position={position}
            onClick={handleToggle}
          />
          {isOpen && (
            <VoiceBar
              voiceState={voiceState}
              transcript={transcript}
              onStart={handleStart}
              onStop={handleStop}
              onClose={handleClose}
            />
          )}
        </>
      );

    case 'panel':
      return (
        <>
          <VoiceBubble
            voiceState={voiceState}
            position={position}
            onClick={handleToggle}
          />
          <VoicePanel
            voiceState={voiceState}
            transcript={transcript}
            isOpen={isOpen}
            onStart={handleStart}
            onStop={handleStop}
            onClose={handleClose}
          />
        </>
      );

    default:
      return null;
  }
};
